/**
 * Blog Analytics Component
 * 
 * Client-side component for tracking blog post engagement metrics.
 * Tracks scroll depth, time on page, and other user interactions.
 */

"use client";

import { useEffect, useRef, useState } from "react";
import { trackEvent } from "@/components/providers/PostHogProvider";
import { Id } from "@/convex/_generated/dataModel";

interface BlogAnalyticsProps {
  postId: Id<"blogPosts">;
  postSlug: string;
  postTitle: string;
  authorId: Id<"users">;
  tags: string[];
  wordCount: number;
  sectionCount: number;
}

export default function BlogAnalytics({
  postId,
  postSlug,
  postTitle,
  authorId,
  tags,
  wordCount,
  sectionCount,
}: BlogAnalyticsProps) {
  const [scrollMilestones, setScrollMilestones] = useState<Set<number>>(new Set());
  const [timeMilestones, setTimeMilestones] = useState<Set<number>>(new Set());
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Capture the start time at the beginning of the effect
    const startTime = startTimeRef.current;
    
    // Track initial page view
    trackEvent("blog_post_viewed", {
      post_id: postId,
      post_slug: postSlug,
      post_title: postTitle,
      author_id: authorId,
      tags,
      word_count: wordCount,
      section_count: sectionCount,
    });

    // Scroll depth tracking
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Track 25%, 50%, 75%, 100% milestones
      [25, 50, 75, 100].forEach((milestone) => {
        if (scrollPercentage >= milestone && !scrollMilestones.has(milestone)) {
          setScrollMilestones(prev => new Set(prev).add(milestone));
          trackEvent("blog_scroll_milestone", {
            post_id: postId,
            post_slug: postSlug,
            milestone_percentage: milestone,
          });
        }
      });
    };

    // Time on page tracking
    const timeInterval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      
      // Track 30s, 1m, 2m, 5m milestones
      const milestones = [30, 60, 120, 300];
      milestones.forEach((milestone) => {
        if (elapsedSeconds >= milestone && !timeMilestones.has(milestone)) {
          setTimeMilestones(prev => new Set(prev).add(milestone));
          trackEvent("blog_read_time_milestone", {
            post_id: postId,
            post_slug: postSlug,
            elapsed_seconds: milestone,
          });
        }
      });
    }, 5000); // Check every 5 seconds

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearInterval(timeInterval);

      // Track final time spent on page
      const totalTimeSeconds = Math.floor((Date.now() - startTime) / 1000);
      trackEvent("blog_post_exit", {
        post_id: postId,
        post_slug: postSlug,
        total_time_seconds: totalTimeSeconds,
        max_scroll_percentage: Math.max(...Array.from(scrollMilestones), 0),
      });
    };
  }, [postId, postSlug, postTitle, authorId, tags, wordCount, sectionCount, scrollMilestones, timeMilestones]);

  // This component doesn't render anything visible
  return null;
}

